import React from 'react';
import { Badge } from '@client/components/ui/badge';
import { Check, Lock, ChevronDown, Minus, CircleMinus, AlertTriangle } from 'lucide-react';

import { cn } from '@client/lib/utils';

function useBlockedStepsAlert(steps: PolicyApplicationStep[]) {
  const blockedMessageMap: Record<StepBlockingRule['blockSlug'], string> = {
    'careScout-2-buy-without-partner-policy':
      'In order to complete this application, you need to have a partner with a CareScout Care Assurance policy.',
  };
  const blockedTitleMap: Record<StepBlockingRule['blockSlug'], string> = {
    'careScout-2-buy-without-partner-policy': 'CareScout 2-Buyer Without Partner Policy',
  };
  const blockedStep = steps.find(step => step.status === 'blocked');
  const blockedReason = blockedStep?.metadata?.blockedReason as StepBlockingRule['blockSlug'];
  const blockedMessage = blockedReason ? blockedMessageMap[blockedReason] : '';

  return {
    hasBlockedSteps: !!blockedStep,
    blockedTitle: blockedTitleMap[blockedReason] || 'Some steps are currently blocked',
    blockedMessage:
      blockedMessage ||
      'One or more steps are currently blocked. Please contact support if you are unsure how to proceed.',
  };
}

interface PolicyApplicationStepperProps {
  steps: PolicyApplicationStep[];
  currentStepId: string;
  selectedStepId?: string | null;
  onStepSelect?: (stepId: string | null) => void;
  className?: string;
  renderStepContent?: (step: PolicyApplicationStep) => React.ReactNode;
}

export function PolicyApplicationStepper({
  steps,
  currentStepId,
  selectedStepId,
  onStepSelect,
  className = '',
  renderStepContent,
}: PolicyApplicationStepperProps) {
  const { hasBlockedSteps, blockedMessage, blockedTitle } = useBlockedStepsAlert(steps);

  return (
    <div className={cn('w-full space-y-4', className)}>
      {/* Blocked Steps Alert */}
      {hasBlockedSteps && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">{blockedTitle}</p>
            <p className="text-sm text-amber-700">{blockedMessage}</p>
          </div>
        </div>
      )}
      {steps.map((step, index) => {
        const { isLocked, isCompleted, isSkipped, isCurrent, isSelected, isClickable } =
          getStepStatus({ step, steps, currentStepId, selectedStepId });

        const stepNumber = index + 1;
        const isCurrentAndNotCompleted = isCurrent && !isCompleted && !isLocked;
        const isDownloadStep = step.stepType === 'download-policy-illustration';
        const isFutureStep = !isCurrent && !isCompleted && !isSkipped && !isDownloadStep;

        return (
          <div
            key={step.stepType}
            className={cn(
              'overflow-hidden rounded-lg border bg-white transition-all duration-200',
              isSelected ? 'border-primary-300 shadow-md' : 'border-gray-200',
              isClickable ? 'hover:border-primary hover:shadow-lg' : '',
            )}
          >
            {/* Step Header - Always Visible */}
            <button
              data-testid={`application-step-${step.stepType}`}
              data-step-status={step.status}
              className={cn(
                'flex w-full flex-col p-4 text-left transition-colors sm:flex-row sm:items-center sm:gap-4 sm:p-6',
                isClickable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default',
                isLocked ? 'cursor-not-allowed' : '',
              )}
              onClick={() => {
                if (isClickable && onStepSelect) {
                  onStepSelect(isSelected ? null : step.stepType);
                }
              }}
              disabled={isLocked}
            >
              <div className="flex w-full items-start gap-3 sm:items-center">
                {/* Step Number Circle */}
                <div className="flex-shrink-0">
                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full transition-all sm:h-10 sm:w-10',
                      {
                        'bg-green-100 text-white': isCompleted,
                        'bg-primary-100': isCurrentAndNotCompleted,
                      },
                    )}
                  >
                    {isCompleted ? (
                      <Check
                        className="h-4 w-4 text-green-700"
                        strokeWidth={3}
                      />
                    ) : isLocked || isFutureStep ? (
                      <Lock className="h-4 w-4 text-gray-400" />
                    ) : isSkipped ? (
                      <CircleMinus className="h-4 w-4 text-gray-400" />
                    ) : (
                      <div
                        className={cn(
                          'h-3 w-3 rounded-full',
                          isCurrent ? 'bg-primary ring-primary ring-2 ring-offset-2' : '',
                        )}
                      />
                    )}
                  </div>
                </div>

                {/* Step Title  */}
                <h3
                  className={cn(
                    'break-words text-base font-semibold sm:whitespace-nowrap',
                    isSelected ? 'text-gray-900' : 'text-gray-700',
                    isLocked ? 'text-gray-400' : '',
                  )}
                >
                  Step {stepNumber}: {step.title}
                </h3>
              </div>

              {/* Status Indicators */}
              <div className="mt-2 flex flex-wrap items-center gap-2 sm:ml-auto sm:mt-0">
                {isCompleted && !isCurrent && (
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                    Completed
                  </Badge>
                )}
                {isCurrent && !isLocked && (
                  <div className="flex items-center gap-1">
                    <Badge className="bg-primary-100 text-primary-700 hover:bg-primary-100">
                      Current
                    </Badge>
                    <ChevronDown
                      className={cn(
                        'text-primary-700 h-4 w-4 transition-transform duration-200',
                        selectedStepId === step.stepType ? 'rotate-180' : '',
                      )}
                    />
                  </div>
                )}
                {isSkipped && <Badge variant="secondary">Skipped</Badge>}
              </div>
            </button>

            {/* Expanded Content - Only for Selected Step */}
            {selectedStepId === step.stepType && renderStepContent && !isLocked && (
              <div className="border-primary-200 border-t">
                <div className="p-6 ">{renderStepContent(step)}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function getStepStatus({
  step,
  steps,
  currentStepId,
  selectedStepId,
}: {
  step: PolicyApplicationStep;
  steps: PolicyApplicationStep[];
  currentStepId: string;
  selectedStepId?: string | null;
}) {
  const isLocked = isStepLocked(step, steps);
  const isSkipped = step.status === 'skipped';
  const isCompleted = step.status === 'completed';
  const isCurrent = step.stepType === currentStepId;
  const isSelected = selectedStepId === step.stepType;
  // Special case for download-policy-illustration: always clickable for backward compatibility
  const isDownloadIllustrationStep = step.stepType === 'download-policy-illustration';
  const isClickable =
    !isLocked && (isCompleted || isSkipped || isCurrent || isDownloadIllustrationStep);

  return {
    isLocked,
    isSkipped,
    isCompleted,
    isCurrent,
    isSelected,
    isClickable,
  };
}

function isStepLocked(step: PolicyApplicationStep, steps: PolicyApplicationStep[]): boolean {
  const stepMap = new Map(steps.map(s => [s.stepType, s]));

  if (step.status === 'blocked') {
    return true;
  }

  // Download-policy-illustration step is never blocked - allows backward compatibility
  if (step.stepType === 'download-policy-illustration') {
    return false;
  }

  // Check if download step is required for this step
  const downloadStep = stepMap.get('download-policy-illustration');
  const stepsThatRequireDownload = ['submit-application', 'consent-form', 'review-and-complete'];

  if (downloadStep && stepsThatRequireDownload.includes(step.stepType)) {
    // Always require download step completion, regardless of backward compatibility
    // The download step should be completed or skipped before accessing protected steps
    if (!(downloadStep.status === 'completed' || downloadStep.status === 'skipped')) {
      return true;
    }
  }
  // Step-specific blocking logic
  switch (step.stepType) {
    case 'pre-underwriting-form':
    case 'partner-pre-underwriting-form':
      return false;

    case 'submit-pre-underwriting': {
      const submitPreUnderwriting = stepMap.get('submit-pre-underwriting');
      if (submitPreUnderwriting?.status === 'completed') {
        return true;
      }
      const preUnderwritingForm = stepMap.get('pre-underwriting-form');
      const partnerPreUnderwritingForm = stepMap.get('partner-pre-underwriting-form');
      return !(
        preUnderwritingForm?.status === 'completed' ||
        partnerPreUnderwritingForm?.status === 'completed'
      );
    }

    case 'application-form': {
      const signApplicationForm = stepMap.get('sign-application-form');
      const submitApplication = stepMap.get('submit-application');
      return (
        signApplicationForm?.status === 'in_progress' ||
        signApplicationForm?.status === 'completed' ||
        submitApplication?.status === 'completed'
      );
    }

    case 'sign-application-form': {
      const signApplicationForm = stepMap.get('sign-application-form');
      const submitApplication = stepMap.get('submit-application');
      // Block if already completed (as per your requirement)
      return (
        signApplicationForm?.status === 'completed' || submitApplication?.status === 'completed'
      );
    }

    case 'consent-form': {
      const consentForm = stepMap.get('consent-form');
      return consentForm?.status === 'completed';
    }

    case 'submit-application': {
      const submitApplication = stepMap.get('submit-application');
      return submitApplication?.status === 'completed';
    }

    default:
      return false;
  }
}
